// Attachment type helpers shared by the composer (upload chips) and the
// transcript (inline media vs. file cards).
//
// Filename sniffing rather than a content_type: the attachments table stores
// only name/s3_file_name/url — no MIME type, no byte size — so the extension is
// the only type signal we have on a message we received. It is trustworthy here
// because uploads are restricted to ALLOWED_EXTENSIONS at the picker.

export const ALLOWED_EXTENSIONS = [
  'jpeg', 'jpg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp',
  'pdf', 'doc', 'docx', 'txt', 'csv', 'xls', 'xlsx',
];

export const ACCEPT_FILES = ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',');

// Every extension we treat as a picture — drives which icon a chip shows.
export const IMAGE_EXT_SET = new Set(['jpeg', 'jpg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp']);

// The subset an <img> can actually paint, which is not the same question.
// HEIC/HEIF are deliberately absent: only Safari decodes them, so painting one
// inline gives Chrome and Firefox a broken box (see utilities/heic.js, which
// converts them to JPEG at upload for this exact reason). That conversion only
// covers this composer, so .heic can still arrive from a native app uploading
// directly, or from rows that predate it — those fall back to a file card,
// which still opens fine in a new tab.
export const INLINE_IMAGE_EXT_SET = new Set(['jpeg', 'jpg', 'png', 'gif', 'webp', 'bmp']);

export function extensionFor(attachment) {
  const name = (attachment && attachment.name) || '';
  if (!name.includes('.')) return '';
  return name.split('.').pop().toLowerCase();
}

// Can we paint this in the transcript, as opposed to linking to it?
export function isInlineImage(attachment) {
  return INLINE_IMAGE_EXT_SET.has(extensionFor(attachment));
}

export function iconForAttachment(attachment) {
  const ext = extensionFor(attachment);
  if (IMAGE_EXT_SET.has(ext)) return 'fa-regular fa-file-image';
  if (ext === 'pdf') return 'fa-regular fa-file-pdf';
  if (['doc', 'docx'].includes(ext)) return 'fa-regular fa-file-word';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'fa-regular fa-file-excel';
  return 'fa-regular fa-file';
}

export function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
